// 🐾 펫 시스템 타입 정의

import { IPhotoCard } from "@typings/photocard/photocard.interface";

// ===== 기본 펫 관련 타입 =====
export interface EvolutionOption {
  id?: string;
  requiredLevel: number;
  name?: string;
  imgURL: string;
  weight?: number; // 선택 가중치(옵션)
  isActive?: boolean;
}

export interface PetTemplate {
  id: string;
  title: string; // 대표 이름
  name?: string; // 커스텀 이름
  category: string; // 종류 (개, 고양이, 새 등)
  imgURL: string; // 기본 이미지
  rarity: 'common' | 'rare' | 'epic' | 'legendary';

  // 랜덤 대사 목록
  dialogues: string[];

  // 성격 키워드 풀
  personalities: string[];

  // 진화 트리 설정
  // 레벨 키 ("level1", "level2" ...) 별 진화 옵션 배열
  evolutionTrees?: {
    [levelKey: string]: EvolutionOption[];
  };

  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface PetDefault {
  maxHunger: number; // 최대 배고픔 수치
  hungerDecay: number; // 시간당 배고픔 감소량
  maxExp: number; // 경험치 배율
  isActive: boolean; // 활성화 여부
  backgroundImgURL?: string; // 배경화면 이미지 URL
  customTerms?: {
    petLabel?: string; // '펫' 대신 표시할 커스텀 용어 (예: 포켓몬, 몬스터)
  };
}

/** 커스텀 용어 또는 기본값 '펫' 반환 */
export function getPetLabel(setting?: PetDefault | null): string {
  return setting?.customTerms?.petLabel?.trim() || '펫';
}

export interface UserPet {
  id: string;
  userId: string;
  petTemplateId: string; // 관리자가 등록한 펫 템플릿 ID
  customName: string; // 사용자가 지은 이름
  customImgURL?: string; // 사용자가 지은 이미지 URL
  backgroundImgURL?: string; // 사용자가 지은 배경 이미지 URL
  level: number;
  experience: number;
  hunger: number; // 0-100 (배고픔 수치, 100이 최대 포만감)
  personalities: string[]; // 성격 키워드
  isActive: boolean; // 현재 활성 펫 여부
  createdAt: Date;
  lastFeedTime: Date;
  lastPlayTime: Date;
  ownerCharacter?: IPhotoCard | null; // 소유중인 오너 캐릭터
  // 과거 이미지 기록: 레벨 키 문자열 사용
  legacyPetImgList?: { levelKey: string; imgURL: string }[];
}

// ===== 배경 시스템 =====
export interface PetBackground {
  id: string;
  type: 'shop' | 'custom';
  imgURL: string;
  name: string;
  isActive: boolean;
  // 상점 배경인 경우
  shopItemId?: string;
  price?: number;
  // 커스텀 배경인 경우
  uploadedAt?: Date;
  userId?: string;
}

// ===== 펫 행동 시스템 =====
export interface PetAction {
  id: string;
  name: string; // 행동명 (예: "인사하기" → "방귀끼기"로 변경 가능)
  type: 'greeting' | 'attendance' | 'mission' | 'walk';
  isEnabled: boolean; // 활성화/비활성화
  cooldown: number; // 쿨다운 시간 (분)

  // 행동 실행 시 효과들
  effects: {
    petExp?: number; // 펫 경험치 지급
    userStats?: {
      // 유저 스테이터스 상승
      [statName: string]: number;
    };
    currency?: {
      // 재화 지급
      type: string; // 'point', 'coin', 'gem' 등
      amount: number;
    };
    hungerDecrease?: number; // 배고픔 감소량
    triggerInteraction?: boolean; // 다른 유저 펫과 상호작용 여부
    // 새로운 옵션: 설정된 최대 배고픔 정책 트리거 시 상호작용 출력
    petMaxHunger?: boolean;
  };

  // UI 설정
  icon: string;
  color: string;
  description: string;
}
export interface IpetActionDoc {
  items: PetAction[];
  updatedAt?: any;
}

// ===== 아이템 시스템 =====
export interface PetInventoryItem {
  id: string;
  name: string;
  category: 'pet_food' | 'pet_toy' | 'pet_accessory' | 'other';
  icon: string;
  description: string;
  quantity: number;
  // 펫 아이템인 경우
  effectType?: 'hunger' | 'experience' | 'appearance';
  effectValue?: number;
  rarity?: 'common' | 'rare' | 'epic' | 'legendary';
}

// ===== 상호작용 로그 =====
export interface PetInteractionLog {
  id: string;
  userId: string;
  petId: string;
  type:
  | 'user_interaction'
  | 'random_dialogue'
  | 'action'
  | 'growth'
  | 'item_use';
  message: string;
  createdAt: Date;
  // 상호작용인 경우
  targetUserId?: string;
  targetPetId?: string;
  // 랜덤 대사인 경우
  isAutoGenerated?: boolean;
}

// ===== 진화 트리 시스템 =====
export interface EvolutionTree {
  id: string;
  title: string; // 예: "뚜벅이 진화 트리"
  description: string;
  steps: EvolutionStep[];
  isActive: boolean;
}

export interface EvolutionStep {
  step: string; // 단계명 1,2,3...
  petTemplate: string; // 해당 단계의 펫 템플릿 ID
  requireLevel: number; // 해당 단계의 레벨
  requireItems: string[]; // 해당 단계의 아이템
  requireConditions: string[]; // 해당 단계의 조건
}

// ===== 레벨 시스템 =====
export interface LevelSystem {
  id: string;
  name: string; // 예: "기본 레벨 시스템"
  levelTemplate: 'linear' | 'exponential' | 'custom';

  levelRequirements: number[]; // [1000, 2000, 4000, 8000...]
  maxLevel: number; // levelRequirements.length
  isActive: boolean;
  // 레벨업 보상
  levelUpRewards: {
    [level: number]: {
      currency?: number;
      items?: string[];
      unlocks?: string[];
    };
  };
}

// ===== 상호작용 문구 시스템 =====
export interface InteractionTemplate {
  id: string;
  name: string; // 템플릿 이름
  templates: string[]; // ["${나의펫}이 ${상대펫}에게 호감을 표시합니다"]

  // 가중치 (랜덤 선택시 확률)
  weight: number;

  isActive: boolean;
  createdAt: Date;
}

export interface RandomDialogueSystem {
  isEnabled: boolean; // 24시간 랜덤 문구 on/off
  interval: number; // 실행 간격 (기본 24시간)

  // 실행 조건
  conditions: {
    minActiveUsers: number; // 최소 활성 사용자 수
    onlyOwnedPets: boolean; // 주인이 있는 펫만 대상
    excludeInactive: boolean; // 비활성 펫 제외
  };

  // 실행 기록
  lastExecution: Date;
  nextScheduled: Date;
}

// ===== 펫 이동 시스템 =====
export interface PetMovement {
  speed: number; // 이동 속도 (1-10)
  smoothness: number; // 부드러움 정도 (0.1-1.0)
  mouseSensitivity?: number; // 마우스 민감도 (0.1 = 마우스 10 움직이면 펫 1 움직임)
  boundaries: {
    // 이동 가능 영역
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  };
}

// ===== UI 관련 타입 =====
export interface PetViewState {
  activePetId: string | null;
  showInventory: boolean;
  showSettings: boolean;
  backgroundId: string | null;
}

export interface ActionCooldown {
  actionId: string;
  lastUsed: Date;
  cooldownMinutes: number;
}

// ===== API 응답 타입 =====
export interface PetActionResult {
  success: boolean;
  message: string;
  rewards?: {
    experience?: number;
    currency?: number;
    items?: string[];
  };
  newPetState?: Partial<UserPet>;
  interactionLog?: PetInteractionLog;
}
